'use client';

/**
 * Public partner (Trusted Installer) application panel — the body of the form
 * section on /installer-accounts.
 *
 * Replaces the HubSpot form embed that used to live here, at Matthew's
 * request. Submissions now land in our own pending queue and fire the same
 * Make → ClickUp → #admin-requests notification the tax-exemption flow uses,
 * so an application has a reviewable record and an approval that actually
 * does something (Partner Pro pricing) instead of an inbox message.
 *
 * Signing in is required, and is not new friction invented here: approval
 * applies a pricing group to an account, so there has to be an account.
 *
 * The page itself stays static/ISR — every piece of signed-in state below is
 * resolved client-side via `useSession` + the account API.
 */

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { BadgeCheck, Clock, FileText, HardHat, LogIn, UploadCloud, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PARTNER_EXPERTISE_OPTIONS } from '@/validators/partner-application.validator';
import { extractEmailDomain, isCompanyEmailDomain } from '@/lib/email-domain';
import { checkUploadSizes, formatMb, MAX_DOCUMENT_BYTES, MAX_REQUEST_UPLOAD_BYTES } from '@/lib/upload-limits';

const PAGE_PATH = '/installer-accounts#form';

interface PartnerStatus {
  isPartner: boolean;
  hasPending: boolean;
  latestStatus: string | null;
}

interface ApiErrorResponse {
  error?: string;
}

/** The two files the application requires, keyed by their multipart field name. */
const REQUIRED_DOCUMENTS = [
  {
    field: 'w9',
    label: 'W-9',
    noun: 'W-9',
    hint: 'Your completed IRS Form W-9 for the business.',
  },
  {
    field: 'resellerCertificate',
    label: 'Reseller certificate',
    noun: 'reseller certificate',
    hint: 'Your resale or reseller tax certificate. We also use it to set up tax-free purchasing on your account.',
  },
] as const;

type DocumentField = (typeof REQUIRED_DOCUMENTS)[number]['field'];

/** Text fields, in the order the HubSpot form asked for them. */
const TEXT_FIELDS = [
  { name: 'firstName', label: 'First name', autoComplete: 'given-name' },
  { name: 'lastName', label: 'Last name', autoComplete: 'family-name' },
  { name: 'mobilePhone', label: 'Phone number', autoComplete: 'tel' },
  { name: 'company', label: 'Company name', autoComplete: 'organization' },
  { name: 'website', label: 'Company website', autoComplete: 'url' },
  { name: 'expectedMonthlySales', label: 'Expected monthly sales', autoComplete: 'off' },
] as const;

const LONG_FIELDS = [
  {
    name: 'installationLocations',
    label: 'Where do you do installations?',
    hint: 'Cities, counties, or states you cover.',
    rows: 2,
  },
  {
    name: 'superiorSupportAndService',
    label: 'Superior support and service',
    hint: 'How your company delivers the level of service SCW expects of its partners.',
    rows: 4,
  },
  {
    name: 'marketingOfInstallationServices',
    label: 'Marketing of installation services',
    hint: 'How you market your installation services.',
    rows: 4,
  },
] as const;

type TextFieldName = (typeof TEXT_FIELDS)[number]['name'];
type LongFieldName = (typeof LONG_FIELDS)[number]['name'];

interface FormState {
  firstName: string;
  lastName: string;
  mobilePhone: string;
  company: string;
  website: string;
  expectedMonthlySales: string;
  installationLocations: string;
  superiorSupportAndService: string;
  marketingOfInstallationServices: string;
  taxId: string;
  licenseNumbers: string;
}

const EMPTY_FORM: FormState = {
  firstName: '',
  lastName: '',
  mobilePhone: '',
  company: '',
  website: '',
  expectedMonthlySales: '',
  installationLocations: '',
  superiorSupportAndService: '',
  marketingOfInstallationServices: '',
  taxId: '',
  licenseNumbers: '',
};

const FIELD_LABELS: Record<keyof FormState, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  mobilePhone: 'Phone number',
  company: 'Company name',
  website: 'Company website',
  expectedMonthlySales: 'Expected monthly sales',
  installationLocations: 'Where do you do installations?',
  superiorSupportAndService: 'Superior support and service',
  marketingOfInstallationServices: 'Marketing of installation services',
  taxId: 'Tax ID',
  licenseNumbers: 'License number(s) and type(s)',
};

function mapApiError(code: string | undefined): string {
  switch (code) {
    case 'application_already_pending':
      return 'You already have an application under review.';
    case 'validation_error':
      return 'Some answers are missing or too long. Check the highlighted fields and try again.';
    case 'unauthorized':
      return 'Please sign in again to submit your application.';
    case 'RATE_LIMITED':
      return 'Too many attempts. Please wait a minute and try again.';
    case 'w9_required':
      return 'Please attach your W-9.';
    case 'reseller_certificate_required':
      return 'Please attach your reseller certificate.';
    case 'documents_required':
      return 'Please attach your W-9 and reseller certificate.';
    case 'file_too_large':
      return `Each file must be under ${formatMb(MAX_DOCUMENT_BYTES)}.`;
    case 'upload_too_large':
      return `Those files are too large. Please keep them under ${formatMb(MAX_REQUEST_UPLOAD_BYTES)} in total.`;
    case 'unsupported_file_type':
      return 'Please attach each document as a PDF or an image.';
    default:
      return 'Submission failed. Please try again.';
  }
}

/**
 * Ad-click id, so paid-ads attribution survives the swap from the HubSpot form
 * (which captured it via its own hidden field). Query param wins over the
 * cookie HubSpot's tracker sets, since the param is this visit's click.
 */
function readGclid(): string | null {
  if (typeof window === 'undefined') return null;
  const fromQuery = new URLSearchParams(window.location.search).get('gclid');
  if (fromQuery) return fromQuery.slice(0, 255);
  const match = document.cookie.match(/(?:^|;\s*)gclid=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]).slice(0, 255) : null;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section className="my-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      {children}
    </section>
  );
}

function Notice({
  icon,
  tone,
  title,
  children,
}: {
  icon: React.ReactNode;
  tone: 'navy' | 'amber' | 'green';
  title: string;
  children: React.ReactNode;
}) {
  const toneClass =
    tone === 'amber'
      ? 'bg-amber-50 text-amber-700'
      : tone === 'green'
        ? 'bg-green-50 text-green-700'
        : 'bg-[var(--scw-navy)]/10 text-[var(--scw-navy)]';
  return (
    <div className="flex items-start gap-4">
      <span
        className={`mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg ${toneClass}`}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="text-base font-semibold text-[var(--scw-navy)]">{title}</h3>
        <div className="mt-1 text-sm text-[var(--scw-gray-body)]">{children}</div>
      </div>
    </div>
  );
}

function DocumentPicker({
  field,
  label,
  hint,
  file,
  onChange,
}: {
  field: DocumentField;
  label: string;
  hint: string;
  file: File | null;
  onChange: (file: File | null) => void;
}) {
  const inputId = `pa-doc-${field}`;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={inputId}>{label}</Label>
      {file ? (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm">
          <FileText className="size-4 shrink-0 text-[var(--scw-navy)]" />
          <span className="min-w-0 flex-1 truncate text-[var(--scw-navy)]">{file.name}</span>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="rounded p-1 text-[var(--scw-gray-muted)] hover:bg-gray-100 hover:text-[var(--scw-navy)]"
            aria-label={`Remove ${label}`}
          >
            <X className="size-4" />
          </button>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-3 py-2.5 text-sm transition-colors hover:border-[var(--scw-navy)]/40 hover:bg-gray-100"
        >
          <UploadCloud className="size-4 shrink-0 text-[var(--scw-gray-muted)]" />
          <span className="font-medium text-[var(--scw-navy)]">Upload file</span>
          <span className="text-xs text-[var(--scw-gray-muted)]">PDF or image</span>
        </label>
      )}
      <input
        id={inputId}
        type="file"
        accept="application/pdf,image/*"
        className="sr-only"
        onChange={(e) => {
          // Read before clearing: clearing empties this same live FileList.
          const picked = e.target.files?.[0] ?? null;
          e.target.value = '';
          if (picked) onChange(picked);
        }}
      />
      <p className="text-xs text-[var(--scw-gray-muted)]">{hint}</p>
    </div>
  );
}

export function PartnerApplicationPanel() {
  const { data: session, status: sessionStatus } = useSession();
  const [status, setStatus] = useState<PartnerStatus | null>(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [expertise, setExpertise] = useState<string[]>([]);
  const [codeOfConduct, setCodeOfConduct] = useState(false);
  const [resellerAcknowledged, setResellerAcknowledged] = useState(false);
  const [documents, setDocuments] = useState<Record<DocumentField, File | null>>({
    w9: null,
    resellerCertificate: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const accountEmail = session?.user?.email ?? '';
  const accountDomain = accountEmail ? extractEmailDomain(accountEmail) : null;
  // Not a gate any more, only the size of what approval grants: a company
  // domain covers everyone there, a personal one covers this account.
  const isPersonalEmail = Boolean(accountEmail) && !isCompanyEmailDomain(accountDomain);

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/account/partner-application');
      setStatus(res.ok ? ((await res.json()) as PartnerStatus) : null);
    } catch {
      setStatus(null);
    } finally {
      setStatusLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (sessionStatus !== 'authenticated') return;
    void loadStatus();
  }, [sessionStatus, loadStatus]);

  function setField(name: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function toggleExpertise(option: string) {
    setExpertise((prev) =>
      prev.includes(option) ? prev.filter((entry) => entry !== option) : [...prev, option],
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const missing = (Object.keys(form) as (keyof FormState)[]).filter(
      (key) => form[key].trim().length === 0,
    );
    if (missing.length > 0) {
      setError(`Please fill in: ${missing.map((key) => FIELD_LABELS[key]).join(', ')}.`);
      return;
    }
    const missingDocuments = REQUIRED_DOCUMENTS.filter((doc) => !documents[doc.field]);
    if (missingDocuments.length > 0) {
      setError(`Please attach your ${missingDocuments.map((doc) => doc.noun).join(' and ')}.`);
      return;
    }
    const files = REQUIRED_DOCUMENTS.map((doc) => documents[doc.field]).filter(
      (file): file is File => file !== null,
    );
    const sizeProblem = checkUploadSizes(files);
    if (sizeProblem) {
      setError(sizeProblem);
      return;
    }
    if (!codeOfConduct || !resellerAcknowledged) {
      setError('Please accept both agreements to submit your application.');
      return;
    }

    setSubmitting(true);
    try {
      const body = new FormData();
      body.append(
        'application',
        JSON.stringify({
          ...form,
          codeOfConduct,
          resellerLicenseTaxCertificate: resellerAcknowledged,
          expertise,
          gclid: readGclid() ?? undefined,
        }),
      );
      for (const doc of REQUIRED_DOCUMENTS) {
        const file = documents[doc.field];
        if (file) body.append(doc.field, file);
      }
      const res = await fetch('/api/account/partner-application', { method: 'POST', body });
      if (res.status === 413) {
        setError(mapApiError('upload_too_large'));
        return;
      }
      if (!res.ok) {
        if (res.status === 429) {
          setError(mapApiError('RATE_LIMITED'));
          return;
        }
        const body = (await res.json().catch(() => ({}))) as ApiErrorResponse;
        setError(mapApiError(body.error));
        return;
      }
      setSubmitted(true);
      await loadStatus();
    } catch {
      setError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── signed out ───────────────────────────────────────────────────────────
  if (sessionStatus === 'unauthenticated') {
    return (
      <Shell>
        <Notice icon={<LogIn className="size-5" />} tone="navy" title="Sign in to apply">
          <p>
            Partner pricing is applied to your SCW account, so we need to know which account
            to attach it to. Sign in with your company email to cover your whole company, or
            with a personal one to cover just your own account.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button
              asChild
              className="bg-[var(--scw-orange)] text-white hover:bg-[var(--scw-orange-hover)]"
            >
              <Link href={`/login?callbackUrl=${encodeURIComponent(PAGE_PATH)}`}>Sign in</Link>
            </Button>
            <Button asChild variant="outline">
              <Link href="/register">Create an account</Link>
            </Button>
          </div>
        </Notice>
      </Shell>
    );
  }

  // ── loading ──────────────────────────────────────────────────────────────
  if (sessionStatus === 'loading' || !statusLoaded) {
    return (
      <Shell>
        <div className="h-24 animate-pulse rounded-lg bg-gray-100" />
      </Shell>
    );
  }

  // ── already a partner ────────────────────────────────────────────────────
  if (status?.isPartner) {
    return (
      <Shell>
        <Notice
          icon={<BadgeCheck className="size-5" />}
          tone="green"
          title="You already have partner pricing"
        >
          <p>
            This account is in the Partner Pro group. Partner prices show automatically while
            you are signed in, on product pages and in your cart.
          </p>
        </Notice>
      </Shell>
    );
  }

  // ── under review ─────────────────────────────────────────────────────────
  if (submitted || status?.hasPending) {
    return (
      <Shell>
        <Notice icon={<Clock className="size-5" />} tone="amber" title="Application under review">
          <p>
            Thanks. Your application is with our team, and we will email you at {accountEmail}{' '}
            once it has been reviewed.
          </p>
        </Notice>
      </Shell>
    );
  }

  // ── the application form ─────────────────────────────────────────────────
  return (
    <Shell>
      <form onSubmit={submit} className="space-y-6">
        <div>
          <h3 className="text-base font-semibold text-[var(--scw-navy)]">
            Apply to the Trusted Installer program
          </h3>
          <p className="mt-1 text-sm text-[var(--scw-gray-body)]">
            Applying as <span className="font-medium">{accountEmail}</span>.
          </p>
          <div className="mt-3 flex items-start gap-3 rounded-lg bg-[var(--scw-navy)]/5 p-3 text-sm text-[var(--scw-gray-body)]">
            <HardHat className="mt-0.5 size-4 shrink-0 text-[var(--scw-navy)]" />
            <p>
              {isPersonalEmail ? (
                <>
                  You are applying with a personal email address, so approval gives partner
                  pricing on this account only. To cover your whole company, apply from your
                  company email instead.
                </>
              ) : (
                <>
                  Approval gives partner pricing to everyone at{' '}
                  <span className="font-medium">{accountDomain}</span>, including teammates who
                  create an account later.
                </>
              )}
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {TEXT_FIELDS.map((field) => (
            <div key={field.name} className="space-y-1.5">
              <Label htmlFor={`pa-${field.name}`}>{field.label}</Label>
              <Input
                id={`pa-${field.name}`}
                autoComplete={field.autoComplete}
                value={form[field.name as TextFieldName]}
                onChange={(e) => setField(field.name, e.target.value)}
              />
            </div>
          ))}
        </div>

        {LONG_FIELDS.map((field) => (
          <div key={field.name} className="space-y-1.5">
            <Label htmlFor={`pa-${field.name}`}>{field.label}</Label>
            <Textarea
              id={`pa-${field.name}`}
              rows={field.rows}
              value={form[field.name as LongFieldName]}
              onChange={(e) => setField(field.name, e.target.value)}
            />
            <p className="text-xs text-[var(--scw-gray-muted)]">{field.hint}</p>
          </div>
        ))}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="pa-taxId">Tax ID</Label>
            <Input
              id="pa-taxId"
              value={form.taxId}
              onChange={(e) => setField('taxId', e.target.value)}
            />
            <p className="text-xs text-[var(--scw-gray-muted)]">
              The program is tax-free, so it requires a registered business entity.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pa-licenseNumbers">License number(s) and type(s)</Label>
            <Textarea
              id="pa-licenseNumbers"
              rows={2}
              value={form.licenseNumbers}
              onChange={(e) => setField('licenseNumbers', e.target.value)}
            />
            <p className="text-xs text-[var(--scw-gray-muted)]">
              Most states expect a state alarm license and a low voltage electrical license.
            </p>
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-[var(--scw-navy)]">
            Expertise <span className="font-normal text-[var(--scw-gray-muted)]">(optional)</span>
          </legend>
          <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            {PARTNER_EXPERTISE_OPTIONS.map((option) => (
              <label
                key={option}
                className="flex cursor-pointer items-start gap-2 text-sm text-[var(--scw-gray-body)]"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 shrink-0 rounded border-gray-300"
                  checked={expertise.includes(option)}
                  onChange={() => toggleExpertise(option)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-[var(--scw-navy)]">Documents</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            {REQUIRED_DOCUMENTS.map((doc) => (
              <DocumentPicker
                key={doc.field}
                field={doc.field}
                label={doc.label}
                hint={doc.hint}
                file={documents[doc.field]}
                onChange={(file) => setDocuments((prev) => ({ ...prev, [doc.field]: file }))}
              />
            ))}
          </div>
        </fieldset>

        <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <label className="flex cursor-pointer items-start gap-2.5 text-sm text-[var(--scw-gray-body)]">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 rounded border-gray-300"
              checked={codeOfConduct}
              onChange={(e) => setCodeOfConduct(e.target.checked)}
            />
            <span>
              I have read and agree to the SCW Trusted Installer code of conduct set out above on
              this page.
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2.5 text-sm text-[var(--scw-gray-body)]">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 rounded border-gray-300"
              checked={resellerAcknowledged}
              onChange={(e) => setResellerAcknowledged(e.target.checked)}
            />
            <span>
              I understand the installer program is tax-free, and that the reseller certificate I
              attached above is reviewed separately before my purchases become tax-exempt.
            </span>
          </label>
        </div>

        {error && (
          <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={submitting}
          className="bg-[var(--scw-orange)] text-white hover:bg-[var(--scw-orange-hover)]"
        >
          {submitting ? 'Submitting…' : 'Submit application'}
        </Button>
      </form>
    </Shell>
  );
}
